import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import type { BundleId } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { scrollToSection } from '../../utils/scroll';
import { SHOP_INTENT_EVENT } from '../../utils/shopIntent';
import type { ShopIntent } from '../../utils/shopIntent';
import { BundleSelector } from './BundleSelector';
import { BuildYourOwn } from './BuildYourOwn';
import { PaymentTrust } from './PaymentTrust';
type Tab = 'systems' | 'byo';

/** Homepage purchase section: system selector or any-quantity builder, side by side as tabs. */
export function ShopTabs() {
  const {
    t
  } = useLanguage();
  const [tab, setTab] = useState<Tab>('systems');
  const [request, setRequest] = useState<{
    id: BundleId;
    n: number;
  } | null>(null);
  const refs: Record<Tab, React.RefObject<HTMLButtonElement>> = {
    systems: useRef<HTMLButtonElement>(null),
    byo: useRef<HTMLButtonElement>(null)
  };

  // Quick-pick choices from the hero open the matching tab and pre-select the system.
  useEffect(() => {
    const onIntent = (e: Event) => {
      const intent = (e as CustomEvent<ShopIntent>).detail;
      if (intent === 'byo') {
        setTab('byo');
      } else {
        setTab('systems');
        setRequest(prev => ({
          id: intent,
          n: (prev?.n ?? 0) + 1
        }));
      }
    };
    window.addEventListener(SHOP_INTENT_EVENT, onIntent);
    return () => window.removeEventListener(SHOP_INTENT_EVENT, onIntent);
  }, []);
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'systems',
    label: t('shop.tabSystems')
  }, {
    id: 'byo',
    label: t('shop.tabByo')
  }];
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next: Tab = tab === 'systems' ? 'byo' : 'systems';
    setTab(next);
    refs[next].current?.focus();
  };
  return <div data-id="element-907" data-mp="3vha3ybvp7mxu">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-908" data-mp="3vhjt5x8x5q2h">
        <div role="tablist" aria-label="Purchase options" onKeyDown={onKey} className="inline-flex rounded-xl border mf-hairline bg-graphite p-1" data-id="element-909" data-mp="43nwzu7xju0a">
          {tabs.map(tb => <button key={tb.id} ref={refs[tb.id]} id={`tab-${tb.id}`} type="button" role="tab" aria-selected={tab === tb.id} aria-controls={`panel-${tb.id}`} tabIndex={tab === tb.id ? 0 : -1} onClick={() => setTab(tb.id)} className={`h-11 whitespace-nowrap rounded-lg px-5 font-display text-[15px] font-semibold uppercase tracking-[0.08em] transition-colors duration-150 ${tab === tb.id ? 'bg-marine text-white' : 'text-titanium hover:text-white'}`} data-id="element-910" data-mp="3rg84pv1s7avv">
              {tb.label}
            </button>)}
        </div>
        <button type="button" onClick={() => scrollToSection('find-system')} className="inline-flex items-center gap-1.5 text-sm text-ice hover:text-white" data-id="element-911" data-mp="2r7rv8ye6aejg">
          {t('shop.notSure')} <ArrowDownIcon className="h-4 w-4" aria-hidden data-id="element-912" data-mp="308yg1jnuubii" />
        </button>
      </div>

      <AnimatePresence mode="wait" data-id="element-913" data-mp="2v93oddw99hwe">
        <motion.div key={tab} id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-10" data-id="element-914" data-mp="2va4zfid9u4e4">
          {tab === 'systems' ? <BundleSelector request={request} data-id="element-915" data-mp="2ci2qyuas9uey" /> : <BuildYourOwn data-id="element-916" data-mp="2chsvwsavsx2e" />}
        </motion.div>
      </AnimatePresence>

      <PaymentTrust className="mt-10 border-t mf-hairline pt-6" data-id="element-917" data-mp="1os8foh644dwt" />
    </div>;
}